import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Gift, Copy, Check, Users, DollarSign, ArrowLeft, Share2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Referrals() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [referralData, setReferralData] = useState(null);
  const [inputCode, setInputCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchReferralData();
  }, [isAuthenticated]);

  const fetchReferralData = async () => {
    try {
      const response = await apiClient.get('/referral/code');
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralData.code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const shareReferral = () => {
    const text = `Join RazientSounds and get $10 off your first booking! Use my referral code: ${referralData.code}`;
    const url = `https://razientsounds.com?ref=${referralData.code}`;
    
    if (navigator.share) {
      navigator.share({ title: 'RazientSounds Referral', text, url });
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success('Referral link copied!');
    }
  };

  const applyCode = async () => {
    if (!inputCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplying(true);
    try {
      const response = await apiClient.post(`/referral/apply?code=${inputCode.trim()}`);
      toast.success(response.data.message);
      setInputCode('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid referral code');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="hover:bg-white/10"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold gradient-text flex items-center gap-2">
              <Gift className="text-violet-400" size={32} />
              Refer & Earn
            </h1>
            <p className="text-white/60">Share RazientSounds with friends and earn credits!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Your Referral Code */}
          <Card className="glass-default border-white/10">
            <CardHeader>
              <CardTitle>Your Referral Code</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-violet-500/20 rounded-xl p-4 text-center">
                  <span className="text-3xl font-mono font-bold tracking-wider">
                    {referralData?.code}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyCode}
                  className="border-white/20 hover:bg-white/10"
                >
                  {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
                </Button>
              </div>
              <Button
                onClick={shareReferral}
                className="w-full bg-violet-600 hover:bg-violet-500"
              >
                <Share2 size={18} className="mr-2" />
                Share with Friends
              </Button>
              <p className="text-sm text-white/60 text-center">
                Your friends get $10 off, and you earn $10 credit for each signup!
              </p>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="glass-default border-white/10">
            <CardHeader>
              <CardTitle>Your Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <Users className="mx-auto mb-2 text-violet-400" size={24} />
                  <p className="text-2xl font-bold">{referralData?.referral_count || 0}</p>
                  <p className="text-sm text-white/60">Referrals</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <DollarSign className="mx-auto mb-2 text-green-400" size={24} />
                  <p className="text-2xl font-bold">${referralData?.total_credits || 0}</p>
                  <p className="text-sm text-white/60">Credits Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Apply Code */}
          <Card className="glass-default border-white/10 md:col-span-2">
            <CardHeader>
              <CardTitle>Have a Referral Code?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  placeholder="Enter referral code"
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                  className="bg-white/5 border-white/10 text-lg font-mono"
                  maxLength={8}
                />
                <Button
                  onClick={applyCode}
                  disabled={applying}
                  className="bg-green-600 hover:bg-green-500"
                >
                  {applying ? 'Applying...' : 'Apply'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* How it Works */}
          <Card className="glass-default border-white/10 md:col-span-2">
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-violet-400">1</span>
                  </div>
                  <h4 className="font-semibold mb-1">Share Your Code</h4>
                  <p className="text-sm text-white/60">Send your unique referral code to friends</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-violet-400">2</span>
                  </div>
                  <h4 className="font-semibold mb-1">Friend Signs Up</h4>
                  <p className="text-sm text-white/60">They create an account using your code</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-violet-400">3</span>
                  </div>
                  <h4 className="font-semibold mb-1">Both Earn $10</h4>
                  <p className="text-sm text-white/60">You both get $10 credit to use on bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
